import { compareSemverVersions } from "./semver.ts";
import type {
  ApplicationReleaseEnvironmentServiceExecutable,
  ApplicationReleaseInstaller,
} from "./environmentServiceExecutablePolicy.ts";

/**
 * What an Application Release tag says about the build it names (ADR-0131).
 *
 * - `release`: plain `X.Y.Z`, published from a tag a maintainer pushes. The only kind the
 *   Application Release Check offers and the mirror carries.
 * - `pull-request-preview`: `X.Y.Z-alpha.<PR><4-digit attempt>`, published by a PR release
 *   dispatch as a tag-only GitHub prerelease and never mirrored.
 * - `retired`: an alpha, beta, or nightly tag published before ADR-0131 removed those variants.
 *   It is still read so an installation built from one compares and activates against a newer
 *   release, but nothing publishes, mirrors, or offers one.
 */
export type ApplicationReleaseKind = "release" | "pull-request-preview" | "retired";

export interface ApplicationReleaseClassification {
  readonly kind: ApplicationReleaseKind;
  readonly version: string;
  readonly githubPrerelease: boolean;
  readonly makeLatest: boolean;
}

export type ApplicationReleasePlatform = "macos" | "windows" | "linux";
export type ApplicationReleaseArchitecture = "arm64" | "x64";
export type ApplicationReleaseFormat = "dmg" | "nsis" | "appimage";
export type ApplicationReleasePlatformStatus = "supported" | "experimental";
export type ApplicationReleaseVerificationStatus = "automated" | "unverified";

export interface ApplicationReleaseArtifactPolicy {
  readonly artifactId: string;
  readonly platform: ApplicationReleasePlatform;
  readonly architecture: ApplicationReleaseArchitecture;
  readonly format: ApplicationReleaseFormat;
  readonly fileName: string;
  readonly platformStatus: ApplicationReleasePlatformStatus;
  readonly verificationStatus: ApplicationReleaseVerificationStatus;
}

export interface ApplicationReleaseArtifactIdentity extends ApplicationReleaseArtifactPolicy {
  readonly identity: string;
  readonly sha256: string;
  readonly size: number;
  readonly mutable: false;
}

const SEMVER_CORE = "(0|[1-9]\\d*)\\.(0|[1-9]\\d*)\\.(0|[1-9]\\d*)";
const RELEASE_VERSION = new RegExp(`^${SEMVER_CORE}$`);
const ALPHA_VERSION = new RegExp(`^${SEMVER_CORE}-alpha\\.(0|[1-9]\\d*)$`);
const RETIRED_BETA_VERSION = new RegExp(`^${SEMVER_CORE}-beta\\.(0|[1-9]\\d*)$`);
const RETIRED_NIGHTLY_VERSION = new RegExp(`^${SEMVER_CORE}-nightly\\.(\\d{8})\\.(0|[1-9]\\d*)$`);
/** Alpha counters with five or more digits reserve a PR number followed by a four-digit attempt. */
const PULL_REQUEST_PREVIEW_COUNTER = /-alpha\.([1-9][0-9]*)([0-9]{4})$/;
const FULL_GIT_COMMIT = /^[0-9a-f]{40}$/;
const SHA256 = /^[0-9a-f]{64}$/;

const isUtcCalendarDate = (value: string): boolean => {
  if (!/^\d{8}$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(4, 6));
  const day = Number(value.slice(6, 8));
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= daysInMonth[month - 1]!;
};

const prerelease = (kind: Exclude<ApplicationReleaseKind, "release">, version: string) => ({
  kind,
  version,
  githubPrerelease: true,
  makeLatest: false,
});

export function classifyApplicationReleaseVersion(
  version: string,
): ApplicationReleaseClassification | undefined {
  if (RELEASE_VERSION.test(version)) {
    return { kind: "release", version, githubPrerelease: false, makeLatest: true };
  }
  if (ALPHA_VERSION.test(version)) {
    return prerelease(
      PULL_REQUEST_PREVIEW_COUNTER.test(version) ? "pull-request-preview" : "retired",
      version,
    );
  }
  if (RETIRED_BETA_VERSION.test(version)) return prerelease("retired", version);
  const nightly = RETIRED_NIGHTLY_VERSION.exec(version);
  if (nightly !== null && nightly[4] !== undefined && isUtcCalendarDate(nightly[4])) {
    return prerelease("retired", version);
  }
  return undefined;
}

export function validateApplicationReleaseIdentity(input: {
  readonly version: string;
  readonly tag: string;
  readonly releaseCommit: string;
}): ApplicationReleaseClassification & {
  readonly tag: string;
  readonly releaseCommit: string;
} {
  const classification = classifyApplicationReleaseVersion(input.version);
  if (classification === undefined) {
    throw new Error("Application Release version must be an exact supported SemVer identity.");
  }
  const expectedTag = `v${input.version}`;
  if (input.tag !== expectedTag) {
    throw new Error(`Application Release tag must be exactly ${expectedTag}.`);
  }
  if (!FULL_GIT_COMMIT.test(input.releaseCommit)) {
    throw new Error("Application Release must bind the full lowercase release commit.");
  }
  return { ...classification, tag: input.tag, releaseCommit: input.releaseCommit };
}

/**
 * Whether a pushed tag may publish an Application Release (ADR-0131).
 *
 * A maintainer names the version by pushing `vX.Y.Z`. It must be a plain release, sort above every
 * other published version — retired prereleases included, so no installation ever sees it as older
 * than what it runs — and its commit must descend from the newest other release, so publishing it
 * never marks older code as the latest release and the mirror never prunes newer code for it. A
 * rerun of the same tag passes again, because the tag is judged against every tag but itself.
 */
export function validatePushedApplicationReleaseTag(input: {
  readonly tag: string;
  readonly releaseCommit: string;
  readonly publishedTags: readonly { readonly tag: string; readonly commit: string }[];
  /** Whether `commit` is an ancestor of (or equal to) the release commit. */
  readonly isAncestorOfReleaseCommit: (commit: string) => boolean;
}): { readonly version: string; readonly tag: string } {
  const version = input.tag.startsWith("v") ? input.tag.slice(1) : "";
  const identity = validateApplicationReleaseIdentity({
    version,
    tag: input.tag,
    releaseCommit: input.releaseCommit,
  });
  if (identity.kind !== "release") {
    throw new Error(`${input.tag} is not a plain vX.Y.Z Application Release tag.`);
  }
  const others = input.publishedTags.flatMap(({ tag, commit }) => {
    const otherVersion = tag.startsWith("v") ? tag.slice(1) : "";
    const classification = classifyApplicationReleaseVersion(otherVersion);
    return tag === input.tag || classification === undefined
      ? []
      : [{ version: otherVersion, commit, kind: classification.kind }];
  });
  const newest = others
    .toSorted((left, right) => compareSemverVersions(left.version, right.version))
    .at(-1);
  if (newest !== undefined && compareSemverVersions(version, newest.version) <= 0) {
    throw new Error(`${input.tag} must be above v${newest.version}, the newest published version.`);
  }
  const newestRelease = others
    .filter((entry) => entry.kind === "release")
    .toSorted((left, right) => compareSemverVersions(left.version, right.version))
    .at(-1);
  if (newestRelease !== undefined && !input.isAncestorOfReleaseCommit(newestRelease.commit)) {
    throw new Error(`${input.tag} does not descend from v${newestRelease.version}.`);
  }
  return { version, tag: input.tag };
}

const APPLICATION_RELEASE_REQUIRED_NATIVE_MATRIX = [
  {
    artifactId: "macos-arm64-dmg",
    platform: "macos",
    architecture: "arm64",
    format: "dmg",
    platformStatus: "supported",
    verificationStatus: "automated",
  },
  {
    artifactId: "macos-x64-dmg",
    platform: "macos",
    architecture: "x64",
    format: "dmg",
    platformStatus: "supported",
    verificationStatus: "unverified",
  },
  {
    artifactId: "linux-x64-appimage",
    platform: "linux",
    architecture: "x64",
    format: "appimage",
    platformStatus: "experimental",
    verificationStatus: "unverified",
  },
] as const satisfies readonly Omit<ApplicationReleaseArtifactPolicy, "fileName">[];

/**
 * Artifacts a release published before its platform was withdrawn.
 *
 * A published manifest is immutable, so withdrawing a platform cannot mean forgetting what its
 * artifact looked like: a release that shipped one of these still has to validate, or it silently
 * leaves the Desktop Client's catalog. Nothing here is ever required, and a new release that
 * declares one is rejected (ADR-0111).
 *
 * Reading accepts these at any version rather than only at the two that published one. Naming the
 * exact versions is the allowlist ADR-0111 deleted, and it would buy nothing: a release cannot
 * publish a withdrawn artifact, because assembly validates against the required inventory alone
 * and no job builds one. The read path's job is to describe a release by its own manifest.
 */
const APPLICATION_RELEASE_WITHDRAWN_NATIVE_MATRIX = [
  {
    artifactId: "windows-x64-nsis",
    platform: "windows",
    architecture: "x64",
    format: "nsis",
    platformStatus: "experimental",
    verificationStatus: "unverified",
  },
] as const satisfies readonly Omit<ApplicationReleaseArtifactPolicy, "fileName">[];

export function applicationReleaseArtifactFileName(
  version: string,
  artifact: Pick<
    ApplicationReleaseArtifactPolicy,
    "platform" | "architecture" | "format" | "verificationStatus" | "platformStatus"
  >,
): string {
  const qualification =
    artifact.platformStatus === "experimental"
      ? "-experimental-unverified"
      : artifact.verificationStatus === "unverified"
        ? "-unverified"
        : "";
  const extension =
    artifact.format === "nsis" ? "exe" : artifact.format === "appimage" ? "AppImage" : "dmg";
  return `AFAR-Research-${version}-${artifact.platform}-${artifact.architecture}${qualification}.${extension}`;
}

/** The PR a `pull-request-preview` tag was published for; undefined for any other kind. */
export function applicationReleasePullRequestNumber(version: string): string | undefined {
  if (classifyApplicationReleaseVersion(version)?.kind !== "pull-request-preview") return undefined;
  return PULL_REQUEST_PREVIEW_COUNTER.exec(version)?.[1];
}

/** Manual installation may replace a PR preview with an ordinary release on the same core. */
export function shouldActivateInstalledApplicationRelease(installed: string, executing: string) {
  if (
    !classifyApplicationReleaseVersion(installed) ||
    !classifyApplicationReleaseVersion(executing)
  )
    return false;
  return (
    compareSemverVersions(installed, executing) > 0 ||
    (applicationReleasePullRequestNumber(executing) !== undefined &&
      applicationReleasePullRequestNumber(installed) === undefined &&
      installed.split("-")[0] === executing.split("-")[0])
  );
}

export function requiredApplicationReleaseArtifacts(
  version: string,
): readonly ApplicationReleaseArtifactPolicy[] {
  return artifactsFromMatrix(version, APPLICATION_RELEASE_REQUIRED_NATIVE_MATRIX);
}

/**
 * Every artifact a manifest may legitimately declare: the ones a release must publish today, plus
 * the ones a release published before its platform was withdrawn. Only the first group is ever
 * required, so this describes what may be read back, never what must be built.
 */
function knownApplicationReleaseArtifacts(
  version: string,
): readonly ApplicationReleaseArtifactPolicy[] {
  return [
    ...requiredApplicationReleaseArtifacts(version),
    ...artifactsFromMatrix(version, APPLICATION_RELEASE_WITHDRAWN_NATIVE_MATRIX),
  ];
}

function artifactsFromMatrix(
  version: string,
  matrix: readonly Omit<ApplicationReleaseArtifactPolicy, "fileName">[],
): readonly ApplicationReleaseArtifactPolicy[] {
  if (classifyApplicationReleaseVersion(version) === undefined) {
    throw new Error("Application Release inventory requires an exact supported release identity.");
  }
  return matrix.map((artifact) => ({
    ...artifact,
    fileName: applicationReleaseArtifactFileName(version, artifact),
  }));
}

export function validateApplicationReleaseArtifactInventory(
  version: string,
  candidates: readonly ApplicationReleaseArtifactIdentity[],
): readonly ApplicationReleaseArtifactIdentity[] {
  const classification = classifyApplicationReleaseVersion(version);
  if (classification === undefined) {
    throw new Error("Application Release inventory requires an exact supported release identity.");
  }
  const required = requiredApplicationReleaseArtifacts(version);
  return validateApplicationReleaseArtifactInventoryAgainstPolicy(
    classification.kind === "pull-request-preview"
      ? "PR preview Application Release"
      : "Application Release",
    required,
    candidates,
  );
}

/** Validate an existing release's declared artifacts without imposing today's publication matrix. */
export function validatePublishedApplicationReleaseArtifactInventory(
  version: string,
  candidates: readonly ApplicationReleaseArtifactIdentity[],
): readonly ApplicationReleaseArtifactIdentity[] {
  if (candidates.length === 0) throw new Error("Published release inventory is empty.");
  const declared = new Set(candidates.map((candidate) => candidate.artifactId));
  return validateApplicationReleaseArtifactInventoryAgainstPolicy(
    "Published Application Release",
    knownApplicationReleaseArtifacts(version).filter((artifact) =>
      declared.has(artifact.artifactId),
    ),
    candidates,
  );
}

function validateApplicationReleaseArtifactInventoryAgainstPolicy(
  label: string,
  required: readonly ApplicationReleaseArtifactPolicy[],
  candidates: readonly ApplicationReleaseArtifactIdentity[],
): readonly ApplicationReleaseArtifactIdentity[] {
  const requiredById = new Map(required.map((artifact) => [artifact.artifactId, artifact]));
  const seenIds = new Set<string>();
  const seenFileNames = new Set<string>();

  for (const candidate of candidates) {
    if (seenIds.has(candidate.artifactId) || seenFileNames.has(candidate.fileName)) {
      throw new Error(`${label} inventory contains a duplicate asset: ${candidate.artifactId}.`);
    }
    seenIds.add(candidate.artifactId);
    seenFileNames.add(candidate.fileName);

    const policy = requiredById.get(candidate.artifactId);
    if (policy === undefined) {
      throw new Error(`${label} inventory contains an unexpected asset: ${candidate.artifactId}.`);
    }
    if (!Number.isSafeInteger(candidate.size) || candidate.size < 1) {
      throw new Error(`${label} inventory contains an empty asset: ${candidate.fileName}.`);
    }
    if (candidate.mutable !== false) {
      throw new Error(`${label} inventory contains a mutable asset: ${candidate.fileName}.`);
    }
    if (!SHA256.test(candidate.sha256)) {
      throw new Error(`${label} inventory contains a malformed digest: ${candidate.fileName}.`);
    }
    if (candidate.identity !== `sha256:${candidate.sha256}`) {
      throw new Error(
        `${label} inventory contains a digest-mismatched asset: ${candidate.fileName}.`,
      );
    }
    if (candidate.fileName !== policy.fileName) {
      throw new Error(
        `${label} inventory contains a wrong-version or wrongly classified asset: ${candidate.fileName}.`,
      );
    }
    if (
      candidate.platform !== policy.platform ||
      candidate.architecture !== policy.architecture ||
      candidate.format !== policy.format ||
      candidate.platformStatus !== policy.platformStatus ||
      candidate.verificationStatus !== policy.verificationStatus
    ) {
      throw new Error(
        `${label} inventory contains a malformed asset declaration: ${candidate.artifactId}.`,
      );
    }
  }

  const missing = required.filter((artifact) => !seenIds.has(artifact.artifactId));
  if (missing.length > 0) {
    throw new Error(
      `${label} inventory is missing required assets: ${missing.map((artifact) => artifact.artifactId).join(", ")}.`,
    );
  }
  if (candidates.length !== required.length) {
    throw new Error(`${label} inventory contains an unexpected asset count.`);
  }

  return candidates;
}

/**
 * The one sentence that qualifies an artifact. The GitHub release body and the Application Release
 * Page both render it, so the two can never disagree about what a given artifact is.
 */
export function applicationReleaseArtifactStatus(
  artifact: Pick<ApplicationReleaseArtifactPolicy, "platformStatus" | "verificationStatus">,
): string {
  if (artifact.platformStatus === "experimental") {
    return "Experimental — automated build only; unverified on real hardware";
  }
  return artifact.verificationStatus === "unverified"
    ? "Supported platform — unverified on real Intel hardware"
    : "Supported platform — automated build verification";
}

/**
 * What a release publishes for the command line, as the manifest states it.
 *
 * The digests are rendered from the manifest rather than published as a file
 * beside the assets. A digests file is a second claim about the same bytes: it
 * can disagree with the manifest, and nothing can settle which of the two is the
 * release. Under ADR-0096 there is one digest per artifact and the manifest
 * holds it.
 */
export interface ApplicationReleaseCommandLineAssets {
  readonly environmentServiceExecutables?: readonly ApplicationReleaseEnvironmentServiceExecutable[];
  readonly installers?: readonly ApplicationReleaseInstaller[];
  /**
   * Where the versionless bootstrap installers are served. Passed in rather than imported, because
   * the module that derives website and mirror locations already depends on this one.
   */
  readonly websiteOrigin?: string;
}

const commandLineReleaseNotes = (
  assets: ApplicationReleaseCommandLineAssets,
): readonly string[] => {
  const bound = [...(assets.environmentServiceExecutables ?? []), ...(assets.installers ?? [])];
  if (bound.length === 0 || assets.websiteOrigin === undefined) return [];
  // The one-liner names the website, not this release: it is versionless on purpose, and the
  // website is the only place that can resolve "the current release" now that the Application
  // Release Mirror is tag-scoped and prunes what a newer release supersedes.
  return [
    "## Command line",
    "",
    "```",
    `curl -fsSL ${assets.websiteOrigin}/install.sh | sh`,
    "```",
    "",
    `Windows: \`irm ${assets.websiteOrigin}/install.ps1 | iex\``,
    "",
    `Every asset below is bound by this release's manifest, and is served from the Application Release Mirror:`,
    "",
    ...bound.map((asset) => `- \`${asset.fileName}\` — \`sha256:${asset.sha256}\``),
    "",
  ];
};

export function applicationReleaseNotes(
  version: string,
  commandLineAssets: ApplicationReleaseCommandLineAssets = {},
): string {
  const classification = classifyApplicationReleaseVersion(version);
  if (classification === undefined) {
    throw new Error("Application Release notes require an exact supported release identity.");
  }
  const artifacts = requiredApplicationReleaseArtifacts(version);
  return [
    `# AFAR Research v${version}`,
    "",
    classification.kind === "release"
      ? "A development release: the Desktop Client and the Host Web Client it serves are development surfaces; AFAR Cloud is the production surface."
      : "This is a GitHub prerelease and is never eligible for the latest-release marker.",
    "",
    "This release is distributed without platform publisher signing or notarization.",
    "",
    ...commandLineReleaseNotes(commandLineAssets),
    "## Native downloads",
    "",
    ...artifacts.map(
      (artifact) => `- \`${artifact.fileName}\` — ${applicationReleaseArtifactStatus(artifact)}.`,
    ),
    "",
    "Linux remains an Experimental Desktop Platform and is unverified on real hardware.",
    "Intel macOS remains unverified until hands-on evidence exists.",
    "Corrections are published as a new version; published tags and assets are never replaced.",
    "",
  ].join("\n");
}

/**
 * Whether a build is a production Application Release or a development one.
 *
 * Production is a release published from a pushed tag, or a retired variant published before
 * ADR-0131; the Application Release Mirror carries it. Development is everything else — a build
 * run from a checkout, and a PR preview, which is published only as a tag and never mirrored. Surfaces that
 * exist for maintainers (reaching a machine over SSH, pairing by link) are offered only in
 * development builds, so a production build shows its users the routes they are meant to use.
 */
export type ApplicationReleaseChannel = "production" | "development";

export function resolveApplicationReleaseChannel(input: {
  readonly version: string;
  readonly isDevelopmentBuild: boolean;
}): ApplicationReleaseChannel {
  if (input.isDevelopmentBuild) return "development";
  if (classifyApplicationReleaseVersion(input.version) === undefined) return "development";
  if (applicationReleasePullRequestNumber(input.version) !== undefined) return "development";
  return "production";
}
